import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllEmployee,
  selectAllEmployee,
} from "../../../redux/users/usersSlice";
import { selectAdmin } from "../../../redux/admin/adminSlice";
import { Icon } from "@iconify-icon/react/dist/iconify.mjs";
import Loader from "../../custom-components/Loader/Loader";
import UserEditModal from "../../employee/UserEditModal";
import PopConfirm from "../../company/PopConfirm";
import { deleteUser } from "../../../utils/api/users";
import { toast } from "react-toastify";

function CompanyEmployee({ companyId,setRefresh }) {
  const dispatch = useDispatch();
  const employees = useSelector(selectAllEmployee);
  const admin = useSelector(selectAdmin);

  const [page, setPage] = useState(1); // Track current page
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true); // Check if there are more employees to load
  const [scrollLoading, setScrollLoading] = useState(false); // To handle scroll loading
  const [allEmployees, setAllEmployees] = useState([]);
  const [user, setUser] = useState(null); // Track user for edit or delete
  const [openModal, setOpenModal] = useState(false); // To open the edit user modal
  const [confirmPopup, setConfirmPopup] = useState(false); // To show delete confirmation popup
  const tableBodyRef = useRef(null); // Reference for scrolling the table body

  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true);
      try {
        const result = await dispatch(getAllEmployee({ companyId, page }));
        const response = result.payload;
        if(!response.hasOwnProperty('status')){
          const newEmployees = response;
          if (newEmployees.length < 10) setHasMore(false);

          setAllEmployees((prevEmployees) => [...prevEmployees, ...newEmployees]);
        }
        
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, [dispatch, companyId, page]);

  useEffect(() => {
    if (allEmployees.length > 0 && tableBodyRef.current) {
      tableBodyRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [allEmployees]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target.documentElement;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !loading) {
      setScrollLoading(true);
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      await deleteUser(user.id);
      toast.success("User Deleted");
      setPage(1); // Reset to first page
      setHasMore(true); // Reset to have more pages if deletion succeeds
      setAllEmployees([]); // Clear the list to reload all employees after deletion
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="w-[800px]">
      <div>
        <div className="flex gap-2">
          <h1 className="mb-4 text-2xl font-semibold">All Employees</h1>
        </div>
        {!loading ? (
          allEmployees?.length > 0 ? (
            <>
              <div className="overflow-auto max-h-[400px]">
                {/* Scrollable table body */}
                <table className="min-w-full border border-collapse border-gray-300 shadow-lg table-auto">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2 border border-gray-300">Name</th>
                      <th className="px-4 py-2 border border-gray-300">
                        Email
                      </th>
                      {admin.role === "administrator" && (
                        <th className="px-4 py-2 border border-gray-300">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody ref={tableBodyRef}>
                    {allEmployees.map((employee, index) => (
                      <tr
                        key={employee.id}
                        className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
                      >
                        <td className="px-4 py-2 border border-gray-300">
                          {employee.name}
                        </td>
                        <td className="px-4 py-2 border border-gray-300">
                          {employee.email}
                        </td>
                        {admin.role === "administrator" && (
                          <td className="px-4 py-2 border border-gray-300 text-center">
                            <div className="flex justify-center items-center gap-2">
                              <Icon
                                onClick={() => {
                                  setUser(employee);
                                  setOpenModal(true);
                                }}
                                className="text-lg text-blue-700 cursor-pointer"
                                icon="fluent:clipboard-text-edit-48-regular"
                              />
                              <Icon
                                onClick={() => {
                                  setUser(employee);
                                  setConfirmPopup(true);
                                }}
                                className="text-lg text-gray-700 cursor-pointer"
                                icon="mingcute:delete-2-fill"
                              />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Load More Button */}
              {hasMore && !loading && (
                <button
                  onClick={handleLoadMore}
                  className="w-full py-2 mt-4 text-white bg-blue-500 hover:bg-blue-600"
                >
                  Load More
                </button>
              )}

              {/* Edit Modal */}
              {openModal && (
                <UserEditModal
                  setRefresh={setRefresh}
                  closeModal={() => setOpenModal(false)}
                  user={user}
                />
              )}

              {/* Delete Confirmation Popup */}
              {confirmPopup && (
                <PopConfirm
                  closePopup={() => setConfirmPopup(false)}
                  confirm={handleDeleteUser}
                  item={user}
                />
              )}
            </>
          ) : (
            <p>No employees found</p>
          )
        ) : (
          <div className="my-28">
            <Loader size={"w-24 h-24"} />
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyEmployee;
